/**
 * Copy-to-clipboard for BSEE — markup-driven, no page JS required.
 *
 * Mark any button with the text to copy, or a selector pointing at the element
 * holding it:
 *
 *   <button data-bsee-copy="#some-textarea">Copy</button>   copy that element's value/text
 *   <button data-bsee-copy-text="hello">Copy</button>       copy a literal string
 *
 * Optional:
 *
 *   data-bsee-copy-label="Copied!"   swap the button's text on success (restored after)
 *   data-bsee-copy-icon="false"      skip the check-icon swap
 *
 * On success the button's <i> icon flips to a check for ~1.2s, then restores —
 * the feedback pattern ca/page/snippets.php established and three other pages
 * then re-implemented by hand. That duplication is why this module exists: the
 * behavior is markup-driven now, so a page opts in with an attribute.
 *
 * WHY CLIPBOARDJS AND NOT navigator.clipboard: the async Clipboard API needs a
 * secure context (https or localhost) and silently rejects elsewhere. ClipboardJS
 * falls back to a hidden-textarea + execCommand copy, so the button keeps working
 * on plain http and in older browsers. ClipboardJS was already bundled here and
 * unused — the hand-rolled versions each re-solved a subset of what it does.
 *
 * NOT in the `arg` profile: public/rg keeps its own copy helper. That bundle is
 * deliberately trimmed to jQuery + profanity-cleaner (see arg.ts provenance), and
 * pulling ClipboardJS in for one button would undo that.
 *
 * @see https://clipboardjs.com/
 */

import ClipboardJS from 'clipboard';

const SELECTOR = '[data-bsee-copy], [data-bsee-copy-text]';
const FEEDBACK_MS = 1200;

/** The text a trigger points at, before any prefix is applied. */
const readSource = (trigger: HTMLElement): string => {
    const literal = trigger.dataset.bseeCopyText;
    if (literal !== undefined) {
        return literal;
    }

    const selector = trigger.dataset.bseeCopy;
    if (!selector) {
        return '';
    }

    const source = document.querySelector<HTMLElement>(selector);
    if (!source) {
        return '';
    }

    if (source instanceof HTMLInputElement || source instanceof HTMLTextAreaElement) {
        return source.value;
    }

    return source.textContent ?? '';
};

/**
 * Resolve what a trigger should copy. An explicit literal wins; otherwise follow
 * the selector to an element and read its value (input/textarea) or its text.
 *
 * `data-bsee-copy-prefix` puts another element's text in front of the result, joined
 * by a newline. That lets a "copy with headers" button reuse the SAME source as the
 * plain copy button next to it, instead of the page emitting every value twice.
 */
const readText = (trigger: HTMLElement): string => {
    const text = readSource(trigger);

    const prefixSelector = trigger.dataset.bseeCopyPrefix;
    if (!prefixSelector) {
        return text;
    }

    const prefixEl = document.querySelector<HTMLElement>(prefixSelector);
    if (!prefixEl) {
        return text;
    }

    const prefix = prefixEl instanceof HTMLInputElement || prefixEl instanceof HTMLTextAreaElement
        ? prefixEl.value
        : (prefixEl.textContent ?? '');

    return `${prefix}\n${text}`;
};

/** Flash a check icon and/or a label on the trigger, then put it back. */
const showFeedback = (trigger: HTMLElement): void => {
    const icon = trigger.querySelector('i');
    if (icon && trigger.dataset.bseeCopyIcon !== 'false') {
        const originalClass = icon.className;
        icon.className = 'fa-solid fa-check';
        setTimeout(() => { icon.className = originalClass; }, FEEDBACK_MS);
    }

    const label = trigger.dataset.bseeCopyLabel;
    if (!label) {
        return;
    }

    // Only the trigger's own text node changes, so an icon child survives the swap.
    const textNode = Array.from(trigger.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
    if (!textNode) {
        return;
    }
    const originalText = textNode.textContent;
    textNode.textContent = ` ${label}`;
    setTimeout(() => { textNode.textContent = originalText; }, FEEDBACK_MS);
};

/**
 * One delegated ClipboardJS instance for the whole document. Delegation means
 * buttons added later (AJAX, a re-rendered table) work with no re-init call.
 */
let clipboard: ClipboardJS | null = null;

export const initBseeCopy = (): void => {
    if (clipboard !== null) {
        return;
    }

    clipboard = new ClipboardJS(SELECTOR, {
        text: trigger => readText(trigger as HTMLElement),
    });

    clipboard.on('success', event => {
        event.clearSelection();
        showFeedback(event.trigger as HTMLElement);
    });
};

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initBseeCopy());
    } else {
        initBseeCopy();
    }
}

export { ClipboardJS };
